import { Menu } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import NotificationElement from "components/NotificationElement";
import { useLanguage } from "contexts/language.context";
import React from "react";

export default function NotificationsMenu({
  notifications,
  anchorElement,
  onClose,
  unreadCount,
  nextCursor,
  loadMore,
  loadingMore,
  markRead,
  reading,
  error,
}) {
  const { label } = useLanguage();
  const notificationCount = unreadCount || 0;
  const markAllTemplate = label("mark-all-as-read-with-count") || "Mark all {count} as read";
  const markAllText = markAllTemplate.includes("{count}")
    ? markAllTemplate.replaceAll("{count}", notificationCount.toLocaleString())
    : `${markAllTemplate} (${notificationCount.toLocaleString()})`;

  return (
    <Menu
      anchorEl={anchorElement}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(anchorElement)}
      onClose={onClose}
      sx={{ mt: 2, mr: 1, minWidth: "500px" }}
    >
      <MDBox
        sx={{
          p: 1,
          pt: 0.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <MDTypography variant="h5">
          {label("notification-plural")}
          {notificationCount ? ` (${notificationCount})` : ""}
        </MDTypography>
        {notificationCount ? (
          <MDButton
            variant="gradient"
            color="info"
            size="medium"
            onClick={() => markRead()}
            disabled={reading}
            sx={{
              minWidth: "fit-content",
              px: 2.5,
              py: 0.9,
              borderRadius: "10px",
              fontSize: "0.75rem",
              fontWeight: "700",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {markAllText}
          </MDButton>
        ) : null}
      </MDBox>
      <MDBox sx={{ minWidth: "500px" }}>
        {notifications?.map((notification, index) => (
          <React.Fragment key={`Notification: ${notification.id}`}>
            <div
              style={{
                width: "calc(100% + 32px)",
                height: "1px",
                backgroundColor: "black",
                opacity: 0.05,
                marginLeft: "-16px",
                marginRight: "-16px",
              }}
            ></div>
            <NotificationElement
              notification={notification}
              onRead={markRead}
              disabled={reading}
              handleCloseMenu={onClose}
            />
          </React.Fragment>
        ))}
        {nextCursor != null && (
          <MDBox
            sx={{
              mt: 0.5,
              px: 1.5,
              py: 1.25,
              display: "flex",
              justifyContent: "center",
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "background.default",
            }}
          >
            <MDButton
              onClick={loadMore}
              disabled={loadingMore || reading}
              variant="text"
              color="info"
              size="small"
              startIcon={loadingMore ? <CircularProgress size={15} color="inherit" /> : undefined}
              endIcon={loadingMore ? undefined : <KeyboardArrowDownIcon fontSize="small" />}
              sx={{
                minWidth: 150,
                borderRadius: 2,
                px: 2,
                py: 0.75,
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {loadingMore ? label("loading") || "Loading…" : label("load-more") || "Load more"}
            </MDButton>
          </MDBox>
        )}
        {error && <MDTypography role="alert" color="error" variant="body2">{error}</MDTypography>}
        {!notificationCount && (
          <MDTypography variant="inherit" color="inherit" pr={1} pl={1}>
            {label("no-unread-notifications")}
          </MDTypography>
        )}
      </MDBox>
    </Menu>
  );
}
